/**
 * Alerts and subscriptions contract (F19): following an evidence page and
 * choosing what to hear about. Re-exported from index.ts. Following is free
 * on every plan (ALRT-075).
 *
 * Owner: alerts (F19). Implements ALRT-010 to ALRT-019, ALRT-040 to ALRT-043 and ALRT-061.
 */
import type { AlertTrigger, DashboardAlert } from './index';

export const ALERT_ROUTES = {
  /** POST CreateSubscriptionRequest -> 201 Subscription (new) or 200 (existing) (E45; ALRT-010, ALRT-011). */
  pageSubscriptions: (slug: string) => `/api/v1/evidence-pages/${encodeURIComponent(slug)}/subscriptions`,
  /** GET ?pageSlug= -> 200 SubscriptionListResponse (E132; ALRT-014). */
  subscriptions: '/api/v1/subscriptions',
  /** PATCH UpdateSubscriptionRequest -> 200 Subscription (E133; ALRT-012); DELETE -> 204 (E46; ALRT-013). */
  subscription: (id: string) => `/api/v1/subscriptions/${encodeURIComponent(id)}`,
  /** GET ?unread=true&pageSlug=&limit=&cursor= -> 200 AlertListResponse (E140; ALRT-040). */
  alerts: '/api/v1/alerts',
  /** POST -> 204 (E48; ALRT-041, ALRT-042). */
  alertRead: (id: string) => `/api/v1/alerts/${encodeURIComponent(id)}/read`,
  /** POST {pageSlug?} -> 200 {updated} (E141; ALRT-043). */
  alertsReadAll: '/api/v1/alerts/read-all',
  /** GET -> 200 UnsubscribeLookup (E142); POST (one-click, RFC 8058) -> 204 (E47; ALRT-064, ALRT-065). No sign-in: the token is the credential. */
  unsubscribe: (token: string) => `/api/v1/unsubscribe/${encodeURIComponent(token)}`,
  /** POST, no body -> 204: one visit from an alert email, counted per day only (E131; ALRT-077). */
  alertVisit: '/api/v1/metrics/alert-visit',
} as const;

/** Most alerts one page of the list returns, and the default (ALRT-040). */
export const ALERT_LIST_MAX = 200;
export const ALERT_LIST_DEFAULT = 50;

/** The fixed order of TIME-074, used for stored and returned triggers. */
export const ALERT_TRIGGERS: readonly AlertTrigger[] = ['study_added', 'crossed_null', 'certainty_changed', 'study_retracted'];

export type EmailFrequency = 'off' | 'immediate' | 'daily' | 'weekly';
export const EMAIL_FREQUENCIES: readonly EmailFrequency[] = ['off', 'immediate', 'daily', 'weekly'];

/** ALRT-019: exactly these eight keys; never an email address. */
export interface Subscription {
  id: string;
  pageSlug: string;
  pageTitle: string;
  /** Fixed order: study_added, crossed_null, certainty_changed, study_retracted. */
  triggers: AlertTrigger[];
  emailFrequency: EmailFrequency;
  /** True when the deployment sends email and the account has a verified address. */
  emailAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

/** All four triggers when `triggers` is absent; `weekly` email when absent and email is on, else `off` (ALRT-010). */
export interface CreateSubscriptionRequest {
  triggers?: AlertTrigger[];
  emailFrequency?: EmailFrequency;
}

/** `updatedAt` is the value last read; a stale one is 409 `conflict` (ALRT-012, REL-071). */
export interface UpdateSubscriptionRequest {
  triggers?: AlertTrigger[];
  emailFrequency?: EmailFrequency;
  updatedAt: string;
}

/**
 * Whether the follow dialog may offer email (ALRT-045): `not_offered` while the deployment sends no alert email
 * (then no email choice is shown at all, CONT-048); `no_verified_address` when the account has no verified address.
 */
export interface AlertEmailOffer {
  available: boolean;
  reason: 'not_offered' | 'no_verified_address' | null;
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[];
  email: AlertEmailOffer;
}

/** An alert in the list: the dashboard's shape, plus the snapshot it describes for the change-log anchor (ALRT-046). */
export interface AlertListItem extends DashboardAlert {
  snapshotId: string | null;
}

/** GET ALERT_ROUTES.alerts: newest first; `unreadCount` counts every unread alert the caller can see (ALRT-040). */
export interface AlertListResponse {
  alerts: AlertListItem[];
  unreadCount: number;
  nextCursor: string | null;
}

export interface ReadAllRequest {
  pageSlug?: string;
}

export interface ReadAllResponse {
  updated: number;
}

/** GET ALERT_ROUTES.unsubscribe: what a link stops, for the confirmation page; `unknown` for a token that does not exist. */
export type UnsubscribeLookup =
  | { kind: 'page'; pageTitle: string; emailOff: boolean }
  /** `all`: the monthly note's link, which stops every alert email (ALRT-076). */
  | { kind: 'digest'; frequency: 'daily' | 'weekly' | 'all'; emailOff: boolean }
  | { kind: 'unknown' };

